import { inject, Injectable } from '@angular/core';
import { CartService } from '../cart/cart.service';
import { Observable, timer, forkJoin, of, BehaviorSubject } from 'rxjs';
import { map, debounce, switchMap, first, tap, debounceTime, distinctUntilChanged } from 'rxjs';
import { Product } from '../../models';
import { CustomerProductPrice, CartRowPrice } from '../../models/price';
import { CartRow } from '../../state/cart/types';

import { PriceService } from '../price/price.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { ProductService } from '../products/product.service';
import { CartTotalsService } from '../cart-totals/cart-totals.service';
import { ProductInputQuantityConfigService } from '../product-input-quantity-config/product-input-quantity-config.service';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { BaseViewConfigService } from '../base-view-config/base-view-config.service';

export interface NavbarCartItem {
  product: Product;
  price: CartRowPrice;
  cartRow: CartRow;
}
export interface NavbarCartModel {
  rows: NavbarCartItem[];
  totalValue: number;
  currency: string;
  rowsMapped: { [id: string]: NavbarCartItem };
  totalQuantity: number;
}
@Injectable({
  providedIn: 'root'
})
export class NavbarCartService {
  protected gungModalService = inject(GungModalService);
  protected translateService = inject(TranslateService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  excludeBlockedProducts = true;

  devMode = localStorage.getItem('dev') || false;
  protected data = new BehaviorSubject<NavbarCartModel>({
    rows: [],
    totalValue: 0,
    currency: '',
    rowsMapped: {},
    totalQuantity: 0
  });

  // Switch this to true to optimize cart get products for large carts
  // false -> Uses getFullProductsByIds
  // true -> Uses getProductsByIds
  public optimizeNavbarCartGetProducts: boolean = this.baseViewConfigService.optimizeNavbarCartGetProducts;

  constructor(
    protected cartService: CartService,
    protected productService: ProductService,
    protected priceService: PriceService,
    protected customerService: SelectedCustomerService,
    protected cartTotalsService: CartTotalsService,
    protected productInputQuantityConfigService: ProductInputQuantityConfigService
  ) {
    if (!this.devMode) {
      this.getContent$().subscribe(d => this.data.next(d));
    }
  }

  clearCart() {
    this.cartService.clearCart();
  }

  protected getContent$() {
    return this.cartTotalsService.getTotals().pipe(
      debounceTime(500),
      switchMap(totals => forkJoin([of(totals), this.cartService.getCurrentCart().pipe(first())])),
      switchMap(([totals, rows]) => {
        const idQtyMap = rows.reduce((acc, curr) => ({ ...acc, [curr.productId]: curr.qty }), {});
        const ids = Object.keys(idQtyMap);
        return forkJoin({
          rows: of(rows),
          products: ids.length > 0
            ? (this.optimizeNavbarCartGetProducts
              ? this.productService.getProductsByIds(ids).pipe(first())
              : this.productService.getFullProductsByIds(ids, this.excludeBlockedProducts).pipe(first()))
            : of([]),
          prices: this.priceService.getCartRowPricesObservable(rows).pipe(first()),
          totals: of(totals).pipe(first())
        });
      }),
      map(({ rows, products, prices, totals }) => {
        const blockedProductRows: CartRow[] = [];
        const toReturnRows = rows.map(row => {
          const rowProduct = products.find(p => p.id === row.productId);
          const rowPrice = prices.find(
            r => r.productId === row.productId && r.productPartialId === row.productPartialId
          );
          if (!rowProduct || !rowPrice) {
            blockedProductRows.push(row);
            return null;
          }
          const data: NavbarCartItem = {
            product: rowProduct,
            price: rowPrice,
            cartRow: row
          };
          return data;
        }).filter((item): item is NavbarCartItem => item !== null);

        if (blockedProductRows.length > 0) {
          this.gungModalService
            .openBasicModal(
              this.translateService.instant('EXCLUDED_PRODUCTS'),
              this.translateService.instant('EXCLUDED_PRODUCTS_MESSAGE', {
                products: blockedProductRows.map(row => row.productId).join(', ')
              }),
              { backdrop: 'static', keyboard: false },
              false,
              'OK',
              true
            )
            .then(proceed => {
              if (proceed) {
                for (const row of blockedProductRows) {
                  this.cartService.removeRow(row);
                }
              }
            });
        }

        // Max decimals is used for products which can have decimal quantities
        let maxDecimals = 0;
        for (const item of toReturnRows) {
          const decimals = this.productInputQuantityConfigService.showDecimals(item.cartRow.productId, item.product);
          maxDecimals = Math.max(maxDecimals, decimals);
        }

        const totalValue = totals.totalValue;
        const currency = totals.totalCurrency;

        const rowsMapped = {};
        toReturnRows.forEach(row => {
          rowsMapped[row.cartRow.productId + (row.cartRow.productPartialId ? '_' + row.cartRow.productPartialId : '')] =
            row;
        });
        return {
          rows: toReturnRows,
          rowsMapped,
          totalValue,
          currency,
          totalQuantity: parseFloat(totals.totalQuantity.toFixed(maxDecimals))
        };
      })
    );
  }

  getContents(): Observable<NavbarCartModel> {
    return this.data.asObservable();
  }
}
